import React from 'react';
import { BsThreeDots } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';

import { useGetOrganizationMembers } from 'legacy/groups/hooks';
import Loader from 'legacy/common/components/layout/Loader';
import { GroupMemberDto, GroupMemberRoleEnum } from 'legacy/groups/types';
import { Avatar } from 'components/Avatar';
import MemberList from 'components/lists/MemberList';
import MembersListItem from 'components/lists/MemberList/ListItem';
import DownloadCSVButton from 'components/DownloadCSVButton';
import { saveAs } from 'file-saver';
import { toast } from 'react-hot-toast';
import { useExportOrganizationMembersCSV } from 'api/queries/organizations/members';

interface Props {
  organizationId: string;
  search: string;
  setSelectedMember: (member: GroupMemberDto) => void;
  isUserAdmin?: boolean;
}

const MembersList = ({
  organizationId,
  search,
  setSelectedMember,
  isUserAdmin
}: Props) => {
  const { t } = useTranslation(['organizations']);
  const {
    data,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    hasNextPage
  } = useGetOrganizationMembers({
    organizationId,
    role: GroupMemberRoleEnum.MEMBER,
    search
  });

  const { isLoading: isExporting, refetch: exportCSV } =
    useExportOrganizationMembersCSV({
      organizationId,
      enabled: false
    });

  const onSelectMember = (member: GroupMemberDto) => () =>
    setSelectedMember(member);

  const onClickExportCSV = async () => {
    try {
      const result = await exportCSV();
      if (result.data) {
        const today = new Date().toISOString().split('T')[0]; // yyyy-mm-dd format
        saveAs(result.data, `organization-members-${today}.csv`);
      }
    } catch (error) {
      toast.error(t('members.exportMembersFailed'));
    }
  };

  return (
    <MemberList
      title={t('members.membersTitle')}
      description={t('members.membersDescription')}
      onNext={fetchNextPage}
      onPrev={fetchPreviousPage}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      colLayout
      headerContent={
        <div className="flex justify-start">
          <DownloadCSVButton onClick={onClickExportCSV} loading={isExporting} />
        </div>
      }
    >
      {isLoading ? (
        <Loader isLoading />
      ) : (
        data?.map((member) => (
          <MembersListItem
            key={member.id}
            avatar={<Avatar src={member.user.photoUrl} />}
            label={`${member.user.firstName} ${member.user.lastName}`}
            title={`${member.titles ? member.titles.join(', ') : null}`}
            actions={
              isUserAdmin && (
                <button
                  type="button"
                  aria-label={t('members.memberActions')}
                  onClick={onSelectMember(member)}
                >
                  <BsThreeDots size={20} />
                </button>
              )
            }
          />
        ))
      )}
    </MemberList>
  );
};

export default MembersList;
